import { StoresTableProps } from './stores-table.types'
import styles from './stores-table.module.scss'
import cl from 'classnames'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'mobx-stores/store-provider'
import { useEffect } from 'react'
import {
  TableContainer,
  TableHeader,
  TableBodyContainer,
  TableBodyLine,
  PaginationButtons,
  Text1,
  RatingStars,
  formatPrice,
  EntityCount,
  parseStringToNumberAndApplyOnChange
} from '@touche-lts/panel-ui/dist'
import { storesTableWidths } from './stores-table.utils'
import { StoresTableSortProps } from 'mobx-stores'
import { PictureNameLine } from 'components'
import { useGetScrollWidth } from 'hooks/custom-hooks'

const StoresTableComponent = (props: StoresTableProps) => {
  const { className, style } = props

  const {
    storesTableStore: {
      triggerSearch,
      searchResults,
      isColumnMarked,
      getSortDirection,
      handleSortDirectionChange,
      searchProps,
      onSearchPropsChange,
      paginationInfo,
      onPageSelect
    },
    generalStore: { showError },
    routingStore: { push }
  } = useRootStore()

  useEffect(() => {
    triggerSearch({ resetPageNumber: true })
  }, [triggerSearch])

  const {scrollX, handleScroll} = useGetScrollWidth()

  return (
    <TableContainer className={cl(styles.container, className, styles.thinScroll)} style={{...style, overflowX : 'auto'}} onScroll={handleScroll}>
      <TableHeader
        tableColumns={[
          {
            label: 'id',
            widthPx: storesTableWidths.id,
            isColumnMarked: isColumnMarked(StoresTableSortProps.ID),
            sortDirection: getSortDirection(StoresTableSortProps.ID),
            setSortDirection: handleSortDirectionChange(
              StoresTableSortProps.ID
            ),
            inputProps: {
              placeholder: 'Search...',
              value: searchProps.id,
              onChange: parseStringToNumberAndApplyOnChange({
                showError,
                onChange: id => onSearchPropsChange({ id })
              })
            }
          },
          {
            label: 'stores',
            widthPx: storesTableWidths.stores,
            isColumnMarked: isColumnMarked(StoresTableSortProps.NAME),
            sortDirection: getSortDirection(StoresTableSortProps.NAME),
            setSortDirection: handleSortDirectionChange(
              StoresTableSortProps.NAME
            ),
            inputProps: {
              placeholder: 'Search...',
              value: searchProps.name,
              onChange: name => onSearchPropsChange({ name })
            }
          },
          {
            label: 'email',
            widthPx: storesTableWidths.email,
            isColumnMarked: isColumnMarked(StoresTableSortProps.EMAIL),
            sortDirection: getSortDirection(StoresTableSortProps.EMAIL),
            setSortDirection: handleSortDirectionChange(
              StoresTableSortProps.EMAIL
            ),
            inputProps: {
              placeholder: 'Search...',
              value: searchProps.email,
              onChange: email => onSearchPropsChange({ email })
            }
          },
          {
            label: 'ratings',
            widthPx: storesTableWidths.ratings,
            isColumnMarked: isColumnMarked(StoresTableSortProps.RATINGS),
            sortDirection: getSortDirection(StoresTableSortProps.RATINGS),
            setSortDirection: handleSortDirectionChange(
              StoresTableSortProps.RATINGS
            ),
            sliderFilterProps: {
              min: 0,
              max: 5,
              label: 'Rating',
              value: [searchProps.minRating, searchProps.maxRating],
              onChange: ([minRating, maxRating]) =>
                onSearchPropsChange({ minRating, maxRating })
            }
          },
          {
            label: 'active products',
            widthPx: storesTableWidths.products
          },
          {
            label: 'total visitors',
            widthPx: storesTableWidths.visitors
            // isColumnMarked: isColumnMarked(StoresTableSortProps.TOTAL_VISITORS),
            // sortDirection: getSortDirection(
            //   StoresTableSortProps.TOTAL_VISITORS
            // ),
            // setSortDirection: handleSortDirectionChange(
            //   StoresTableSortProps.TOTAL_VISITORS
            // )
          },
          {
            label: 'sold',
            widthPx: storesTableWidths.sold,
            // isColumnMarked: isColumnMarked(StoresTableSortProps.SOLD),
            // sortDirection: getSortDirection(StoresTableSortProps.SOLD),
            // setSortDirection: handleSortDirectionChange(
            //   StoresTableSortProps.SOLD
            // ),
            sliderFilterProps: {
              min: 0,
              max: 5000,
              label: 'Items',
              value: [searchProps.minSold, searchProps.maxSold],
              onChange: ([minSold, maxSold]) =>
                onSearchPropsChange({ minSold, maxSold })
            }
          },
          {
            label: 'total revenue',
            widthPx: storesTableWidths.revenue,
            // isColumnMarked: isColumnMarked(StoresTableSortProps.TOTAL_REVENUE),
            // sortDirection: getSortDirection(StoresTableSortProps.TOTAL_REVENUE),
            // setSortDirection: handleSortDirectionChange(
            //   StoresTableSortProps.TOTAL_REVENUE
            // ),
            sliderFilterProps: {
              min: 0,
              max: 5000,
              label: '$',
              value: [searchProps.minRevenue, searchProps.maxRevenue],
              onChange: ([minRevenue, maxRevenue]) =>
                onSearchPropsChange({ minRevenue, maxRevenue })
            }
          },
          {
            label: 'country',
            widthPx: storesTableWidths.country,
            // isColumnMarked: isColumnMarked(StoresTableSortProps.COUNTRY),
            // sortDirection: getSortDirection(StoresTableSortProps.COUNTRY),
            // setSortDirection: handleSortDirectionChange(
            //   StoresTableSortProps.COUNTRY
            // ),
            inputProps: {
              placeholder: 'Search...',
              value: searchProps.country,
              onChange: country => onSearchPropsChange({ country })
            }
          },
          {
            label: 'status',
            widthPx: storesTableWidths.status,
            // isColumnMarked: isColumnMarked(StoresTableSortProps.COUNTRY),
            // sortDirection: getSortDirection(StoresTableSortProps.COUNTRY),
            // setSortDirection: handleSortDirectionChange(
            //   StoresTableSortProps.COUNTRY
            // ),
            inputProps: {
              placeholder: 'Search...',
              value: searchProps.status,
              onChange: country => onSearchPropsChange({ country })
            }
          }
        ]}
      />
      <TableBodyContainer className={styles.tableBodyContainer}>
        {searchResults.map(store => {
          return (
            <TableBodyLine
              className={store.status === 'DELETED' ? cl(styles.rowColor, className) : cl(styles.tableCursor, className)} style={style}
              key={store.id}
              onClick={() => push(`/stores/${store.id}`)}
              cells={
                [
                  {
                    widthPx: storesTableWidths.id,
                    children: store.id,
                    isColumnMarked: isColumnMarked(StoresTableSortProps.ID),

                  },
                  {
                    widthPx: storesTableWidths.stores,
                    children: (
                      <PictureNameLine
                        imageSrc={store.image?.url}
                        imageBoxShadow
                        name={store.name}
                      />
                    ),
                  },
                  {
                    widthPx: storesTableWidths.email,
                    colorPreset: 'dark',
                    children: <Text1 overflowEllipsis>{store.email}</Text1>
                  },
                  {
                    widthPx: storesTableWidths.ratings,
                    children: <RatingStars rating={store.rating} />
                  },
                  {
                    widthPx: storesTableWidths.products,
                    /** TODO: Implement EntityCount component **/
                    children: (
                      <EntityCount
                        count={store.activeProducts}
                        entity='product'
                      />
                    )
                  },
                  {
                    widthPx: storesTableWidths.visitors,
                    children: (
                      <Text1 overflowEllipsis>{store.totalVisitors}</Text1>
                    )
                  },
                  {
                    widthPx: storesTableWidths.sold,
                    children: <Text1 overflowEllipsis>{store.sold}</Text1>
                  },
                  {
                    widthPx: storesTableWidths.revenue,
                    children: (
                      <Text1 overflowEllipsis>
                        {formatPrice(store.totalRevenue)}
                      </Text1>
                    )
                  },
                  {
                    widthPx: storesTableWidths.country,
                    children: <Text1 overflowEllipsis>{store.country}</Text1>
                  },
                  {
                    widthPx: storesTableWidths.status,
                    children: (
                      <Text1 overflowEllipsis>
                        {store.status}
                      </Text1>
                    )
                  },
                ]}
            />
          )
        })}
      </TableBodyContainer>
      <PaginationButtons
        alwaysShow
        {...paginationInfo}
        onPageSelect={onPageSelect}
        style={{width : scrollX}}
      />
    </TableContainer>
  )
}

const StoresTable = observer(StoresTableComponent)
export { StoresTable }
